/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect, useCallback } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useBatchEditable } from './useBatchEditable';
import {
  isStrEmptyOrNull,
  isObjEmptyOrNull,
  getAbbrvMapFromQID,
  getStrValue,
  getDateValue,
  getEventDateArrayValues,
  getNestedVal,
  setNestedValue,
  addToObj,
  getErrorFromEndpoint,
  getErrorStr,
} from '../UtilityFunctions';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';

interface EndpointData {
  endpoint?: string;
}

interface EndpointDataAccessed extends EndpointData {
  variables: Array<varInfo>;
  reqtype: 'post' | 'get' | 'all';
  reqBody?: { type: string; body: Array<object> };
  endpointType?: 'multi'; // multiple use endpoint, such as occurance endpoints, can be used more than once for the same loan
}

interface EndpointRespData extends EndpointData {
  data?: Record<string, any> | Array<Record<string, any>>;
  error?: string;
}

interface varInfo {
  // type string, date, event date obj, array of event date objects, qid, qid dictionary for lookup, boolean
  type: 'S' | 'D' | 'ED' | 'EDA' | 'Q' | 'QD' | 'B';
  name?: string; // response or request variable name
  setTo?: string; // variable name in loan default codes state - used when name might be the same for another variable in another request
  emtVal: '' | [] | {} | boolean; // default value for variable, resetToEmtVal resets the variable to this value
  reqVarName?: string; // the request variable name to post, if different from name
  resetToEmtVal?: boolean; // if true, resets the variable in the loan default codes state to the emtVal after a get or post
  actions: Array<'get' | 'post' | 'url'>; // (can be multiple, for ex: ["get", "post"] : if get, gets the variable from a response, if post, posts the variable in request, if url, adds variable to the url as a parameter
}

const endpointsAccessed: Record<string, EndpointDataAccessed> = {
  loanInfo: {
    endpoint: 'loan/?',
    reqtype: 'all',
    variables: [
      { name: 'ddrReason', type: 'S', emtVal: '', actions: ['get', 'post'] },
      { name: 'deedInLieuStatus', type: 'S', emtVal: '', actions: ['get', 'post'] },
      { name: 'foreclosureMainStatus', type: 'S', emtVal: '', actions: ['get', 'post'] },
      { name: 'loanType', type: 'S', emtVal: '', actions: ['get'] },
      { name: 'loanTypeKeyword', type: 'S', emtVal: '', actions: ['get'] },
      { name: 'investorDdrReason', type: 'S', emtVal: '', actions: ['get', 'post'] },
    ],
  },
  bankruptcyPaymentInfo: {
    endpoint: process.env.REACT_APP_API_BANKRUPTCY_PAYMENT_INFO,
    reqtype: 'get',
    variables: [
      { name: 'prePetitionPmntRespParty', type: 'S', emtVal: '', actions: ['get'] },
      { name: 'postPetitionPmntRespParty', type: 'S', emtVal: '', actions: ['get'] },
    ],
  },
  bankruptcyActions: {
    endpoint: process.env.REACT_APP_API_BANKRUPTCY_ACTIONS,
    reqtype: 'get',
    variables: [
      {
        name: 'reaffirmationFiledDate',
        type: 'D',
        setTo: 'dates.reaffirmationFiledDate',
        emtVal: '',
        actions: ['get'],
      },
    ],
  },
  bankruptcyDetails: {
    endpoint: process.env.REACT_APP_API_BANKRUPTCY_DETAILS,
    reqtype: 'get',
    variables: [
      { name: 'bkStatus', type: 'S', emtVal: '', actions: ['get'] },
      { name: 'bkChapter', type: 'S', emtVal: '', actions: ['get'] },
      { name: 'bkCaseNum', type: 'S', emtVal: '', actions: ['get'] },
      { name: 'bkFileDate', type: 'D', setTo: 'dates.bkFileDate', emtVal: '', actions: ['get'] },
      { name: 'priorBankruptcy', type: 'S', emtVal: '', actions: ['get'] },
      { name: 'bkFileBy', type: 'S', emtVal: '', actions: ['get'] },
      {
        name: 'priorBankruptcyFiledDate',
        type: 'D',
        setTo: 'dates.priorBankruptcyFiledDate',
        emtVal: '',
        actions: ['get'],
      },
      {
        name: 'dtOfBankruptcyDischarged',
        type: 'D',
        setTo: 'dates.dtOfBankruptcyDischarged',
        emtVal: '',
        actions: ['get'],
      },
      {
        name: 'dtOfDismisOrMfrGrntdOrStayLifted',
        type: 'D',
        setTo: 'dates.dtOfDismisOrMfrGrntdOrStayLifted',
        emtVal: '',
        actions: ['get'],
      },
    ],
  },
  bankruptcyDetailsUpdate: {
    endpoint: process.env.REACT_APP_API_BANKRUPTCY_DETAILS_UPDATE,
    reqtype: 'post',
    variables: [
      { name: 'bkStatus', type: 'S', emtVal: '', actions: ['post'] },
      { name: 'bkChapter', type: 'S', emtVal: '', actions: ['post'] },
      { name: 'bkCaseNum', type: 'S', emtVal: '', actions: ['post'] },
      { name: 'bkFileDate', type: 'D', setTo: 'dates.bkFileDate', emtVal: '', actions: ['post'] },
      { name: 'priorBankruptcy', type: 'S', emtVal: '', actions: ['post'] },
      {
        name: 'priorBankruptcyFiledDate',
        type: 'D',
        setTo: 'dates.priorBankruptcyFiledDate',
        emtVal: '',
        actions: ['post'],
      },
      { name: 'bkFileBy', type: 'S', emtVal: '', actions: ['post'] },
    ],
  },
  servicing: {
    endpoint: process.env.REACT_APP_API_SERVICING,
    reqtype: 'all',
    reqBody: { type: 'post', body: [{ active: 'true' }] },
    variables: [
      { name: 'defaultBankruptcyStatus', type: 'S', emtVal: '', actions: ['get', 'post'] },
      { name: 'fhlmcStatus', type: 'S', emtVal: '', actions: ['get', 'post'] },
      { name: 'fhlmcCutoffActionCode', type: 'S', emtVal: '', actions: ['get', 'post'] },
      { name: 'fnmaCutoffActionCode', type: 'S', emtVal: '', actions: ['get', 'post'] },
      { name: 'investorCode', type: 'S', emtVal: '', actions: ['get'] },
      { name: 'investorCodeDescription', type: 'S', emtVal: '', actions: ['get'] },
      { name: 'investorLoanType', type: 'S', emtVal: '', actions: ['get'] },
      { name: 'investorLoanTypeKeyword', type: 'S', emtVal: '', actions: ['get'] },
    ],
  },
  property: {
    endpoint: process.env.REACT_APP_PROPERTY.replace('live/', ''),
    reqtype: 'all',
    variables: [
      { name: 'propertyConditionCode', type: 'S', emtVal: '', actions: ['get', 'post'] },
      { name: 'propCode', type: 'S', emtVal: '', resetToEmtVal: true, actions: ['url'] },
      { name: 'servicingOccupancy', type: 'S', emtVal: '', actions: ['get', 'post'] },
    ],
  },
  shortsale: {
    endpoint: process.env.REACT_APP_SHORT_SALE.replace('live/', ''),
    reqtype: 'all',
    variables: [{ name: 'shortSaleStatus', type: 'S', emtVal: '', actions: ['get'] }],
  },
  ddsCodesListForLoan: {
    endpoint: process.env.REACT_APP_EVENTS_LIST.replace('live/', ''),
    reqtype: 'get',
    reqBody: { type: 'get', body: [{ parameters: { eventCodes: 'S77' } }] },
    variables: [{ name: 'ddsCodesListForLoan', type: 'EDA', emtVal: [], actions: ['get'] }],
  },
  ddsCode: {
    endpoint: process.env.REACT_APP_ADD_EVENT_REFERENCE.replace('live/', ''),
    reqtype: 'post',
    variables: [
      { name: 'DDSCode', reqVarName: 'refCode', type: 'S', emtVal: '', resetToEmtVal: true, actions: ['post'] },
      { name: 'DDSRefQid', reqVarName: 'refQid', type: 'S', emtVal: '', resetToEmtVal: true, actions: ['post'] },
      { name: 'DDSEventKwd', reqVarName: 'eventKwd', type: 'S', emtVal: '', resetToEmtVal: true, actions: ['post'] },
      {
        name: 'dates.DDSReportingDate',
        reqVarName: 'reportingDate',
        type: 'D',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['post'],
      },
      {
        name: 'dates.DDSEventDate',
        reqVarName: 'date',
        type: 'D',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['post'],
      },
    ],
  },
  DDSDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'S77/occurrence/',
    endpointType: 'multi',
    reqtype: 'post',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['post'],
      },
      { name: 'ddsOccurrence', type: 'S', emtVal: '', resetToEmtVal: true, actions: ['url'] },
      { name: 'active', type: 'B', emtVal: null, resetToEmtVal: true, actions: ['post'] },
      { name: 'reportingDate', type: 'D', emtVal: '', resetToEmtVal: true, actions: ['post'] },
    ],
  },
  investorDDSCodesListForLoan: {
    endpoint: process.env.REACT_APP_EVENTS_LIST.replace('live/', ''),
    reqtype: 'get',
    reqBody: { type: 'get', body: [{ parameters: { eventCodes: 'S7E' } }] },
    variables: [{ name: 'investorDDSCodesListForLoan', type: 'EDA', emtVal: [], actions: ['get'] }],
  },
  investorDDSCode: {
    endpoint: process.env.REACT_APP_ADD_EVENT_REFERENCE.replace('live/', ''),
    reqtype: 'post',
    variables: [
      { name: 'investorDDSCode', reqVarName: 'refCode', type: 'S', emtVal: '', resetToEmtVal: true, actions: ['post'] },
      {
        name: 'investorDDSRefQid',
        reqVarName: 'refQid',
        type: 'S',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['post'],
      },
      {
        name: 'investorDDSEventKwd',
        reqVarName: 'eventKwd',
        type: 'S',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['post'],
      },
      {
        name: 'dates.investorDDSReportingDate',
        reqVarName: 'reportingDate',
        type: 'D',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['post'],
      },
      {
        name: 'dates.investorDDSEventDate',
        reqVarName: 'date',
        type: 'D',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['post'],
      },
    ],
  },
  investorDDSDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'S7E/occurrence/',
    endpointType: 'multi',
    reqtype: 'post',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['post'],
      },
      {
        name: 'ddsOccurrence',
        type: 'S',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['url'],
      },
      {
        name: 'active',
        type: 'B',
        emtVal: null,
        resetToEmtVal: true,
        actions: ['post'],
      },
      { name: 'reportingDate', type: 'D', emtVal: '', resetToEmtVal: true, actions: ['post'] },
    ],
  },
  ForeclosureSaleHeldDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'F75',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.ForeclosureSaleHeldDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
    ],
  },
  DDRDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'SRG',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.DDRDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
    ],
  },
  FHLMCInactiveDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'SRE',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.FHLMCInactiveDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
    ],
  },
  FHLMCReactivateDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'SRF',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.FHLMCReactivateDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
    ],
  },
  CutoffActionDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'SRJ',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.CutoffActionDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
      {
        name: 'CutoffActionCode',
        reqVarName: 'referenceCode',
        type: 'S',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['post'],
      },
      {
        name: 'CutoffActionRefQid',
        reqVarName: 'refQid',
        type: 'S',
        emtVal: '',
        resetToEmtVal: true,
        actions: ['post'],
      },
      {
        name: 'reportingDate',
        reqVarName: 'reportingDate',
        setTo: 'dates.CutoffActionReportingDate',
        type: 'D',
        emtVal: '',
        actions: ['get', 'post'],
      },
    ],
  },
  CompletionDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'SRI',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.CompletionDate.activeDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
      {
        name: 'active',
        type: 'B',
        setTo: 'dates.CompletionDate.active',
        emtVal: true,
        actions: ['get', 'post'],
      },
    ],
  },
  EstimatedForbearanceStartDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'DTM',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.EstimatedForbearanceStartDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
    ],
  },
  EstimatedForbearanceEndDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'FDS',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.EstimatedForbearanceEndDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
    ],
  },
  InvestorDDRDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'SRK',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.InvestorDDRDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
    ],
  },
  ServicingOccuStatusVerifiedDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'SRL',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.ServOccuStatusVerifiedDate.activeDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
      {
        name: 'active',
        setTo: 'dates.ServOccuStatusVerifiedDate.active',
        type: 'B',
        emtVal: true,
        actions: ['get', 'post'],
      },
    ],
  },
  VacantDate: {
    endpoint: process.env.REACT_APP_EVENT_DATES + 'SRM',
    reqtype: 'all',
    variables: [
      {
        name: 'activeDate',
        type: 'D',
        setTo: 'dates.VacantDate',
        emtVal: '',
        actions: ['get', 'post'],
      },
    ],
  },
  ddrReasonOptions: {
    endpoint: process.env.REACT_APP_QID_DDRREASON_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.ddrReasonOptions', emtVal: [], actions: ['get'] }],
  },
  investorDDROptions: {
    endpoint: process.env.REACT_APP_QID_INVESTOR_DDRREASON_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.investorDDROptions', emtVal: [], actions: ['get'] }],
  },
  foreclosureMainStatusOptions: {
    endpoint: process.env.REACT_APP_QID_FORECLOSURE_MAIN_STATUS_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.foreclosureMainStatusOptions', emtVal: [], actions: ['get'] }],
  },
  deedInLieuStatusOptions: {
    endpoint: process.env.REACT_APP_QID_DEED_IN_LIEU_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.deedInLieuStatusOptions', emtVal: [], actions: ['get'] }],
  },
  CutoffActionFHLMCOptions: {
    endpoint: process.env.REACT_APP_QID_CUTOFF_ACTION_CODE_FHLMC_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.CutoffActionFHLMCOptions', emtVal: [], actions: ['get'] }],
  },
  CutoffActionFNMAOptions: {
    endpoint: process.env.REACT_APP_QID_CUTOFF_ACTION_CODE_FNMA_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.CutoffActionFNMAOptions', emtVal: [], actions: ['get'] }],
  },
  FHLMCStatusOptions: {
    endpoint: process.env.REACT_APP_QID_FHLMC_STATUS_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.FHLMCStatusOptions', emtVal: [], actions: ['get'] }],
  },
  DBStatusOptions: {
    endpoint: process.env.REACT_APP_QID_DB_STATUS_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.DBStatusOptions', emtVal: [], actions: ['get'] }],
  },
  BankruptcyStatusOptions: {
    endpoint: process.env.REACT_APP_QID_BANKRUPTCY_STATUS_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.BankruptcyStatusOptions', emtVal: {}, actions: ['get'] }],
  },
  RespPetitionPartyOptions: {
    endpoint: process.env.REACT_APP_QID_RESP_PARTY_PETITION_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.RespPetitionPartyOptions', emtVal: [], actions: ['get'] }],
  },
  DDSCodeOptions: {
    endpoint: process.env.REACT_APP_QID_DDS_CODE_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.DDSCodeOptions', emtVal: [], actions: ['get'] }],
  },
  InvestorDDSCodeOptions: {
    endpoint: process.env.REACT_APP_QID_INVESTOR_DDS_CODE_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.InvestorDDSCodeOptions', emtVal: [], actions: ['get'] }],
  },
  PropertyConditionCodeOptions: {
    endpoint: process.env.REACT_APP_QID_PROP_COND_CODE_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.PropertyCondCodeOptions', emtVal: [], actions: ['get'] }],
  },
  ShortSaleOptions: {
    endpoint: process.env.REACT_APP_QID_SHORT_SALE_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.ShortSaleOptions', emtVal: [], actions: ['get'] }],
  },
  ServicingOccupancyOptions: {
    endpoint: process.env.REACT_APP_QID_SERV_OCCUPANCY_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.ServicingOccupancyOptions', emtVal: [], actions: ['get'] }],
  },
  BankruptcyChapterOptions: {
    endpoint: process.env.REACT_APP_QID_BANKRUPTCY_CHAPTER_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.BankruptcyChapterOptions', emtVal: [], actions: ['get'] }],
  },
  BankruptcyYesNoOptions: {
    endpoint: process.env.REACT_APP_QID_YES_NO_LIST,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.BankruptcyYesNoOptions', emtVal: [], actions: ['get'] }],
  },
  BankruptcyFiledByOptions: {
    endpoint: process.env.REACT_APP_QID_BANKRUPTCY_FILED_BY,
    reqtype: 'get',
    variables: [{ type: 'Q', setTo: 'QIDOptions.BankruptcyFiledByOptions', emtVal: [], actions: ['get'] }],
  },
};

function isEndpointLoanSpecific(endpointName) {
  if (!endpointsAccessed.hasOwnProperty(endpointName)) return true;
  const obj = endpointsAccessed[endpointName];
  const loanSpecific = obj.variables.some((variableInfo) => {
    if (!variableInfo.type.startsWith('Q')) {
      return true;
    }
    return false;
  });
  return loanSpecific;
}

function getValForVarType(val, type) {
  switch (type) {
    case 'S':
      return getStrValue(val);
    case 'D': {
      return getDateValue(val);
    }
    case 'EDA': {
      return getEventDateArrayValues(val);
    }
    case 'QD': {
      return getAbbrvMapFromQID(val);
    }
    default:
      return val;
  }
}

const getValueFromEndpoint = (resData: any, varData: varInfo) => {
  const listType = varData.emtVal.constructor === Array || varData.emtVal.constructor === Object;
  const variable = varData.name;
  const res = resData.constructor === Array && !listType ? resData[0] : resData;
  if (!listType && variable in res) {
    return getValForVarType(res[variable], varData.type);
  } else if (listType) {
    return getValForVarType(res, varData.type);
  }
  return null;
};

function getLoanDefaultInfoState() {
  let newLDIState = {};
  for (const endpoint in endpointsAccessed) {
    newLDIState = getUpdatedNestedState(newLDIState, endpoint);
  }
  return newLDIState;
}

/**
 * returns default loan default codes state with empty variable values
 */
function getUpdatedNestedState(obj, endpoint: string) {
  if (!endpointsAccessed.hasOwnProperty(endpoint)) {
    return obj;
  }
  if (
    endpointsAccessed[endpoint].hasOwnProperty('endpointType') &&
    endpointsAccessed[endpoint].endpointType === 'multi'
  ) {
    obj = setNestedValue(obj, endpoint, {});
  } else {
    const vars = endpointsAccessed[endpoint].variables;
    vars.forEach((variable) => {
      const nameVar = 'setTo' in variable ? variable.setTo : variable.name;
      obj = setNestedValue(obj, nameVar, variable.emtVal);
    });
  }
  return obj;
}

const useLoanDefaultCodesInfo = () => {
  const { loanNumber, propCode } = useLoanContext();
  const { token } = useAuthContext();
  const {
    curr: loanDefaultInfo,
    handleChange,
    isChanged,
    hasChanges,
  } = useBatchEditable<{ [key: string]: any }>({
    initialObj: getLoanDefaultInfoState(),
  });

  const getGetEndpointReqBody = useCallback(
    (endpointName: string) => {
      const obj = endpointsAccessed[endpointName];
      let finalEndpointBody = {};
      if (obj.reqtype === 'get' || obj.reqtype === 'all') {
        finalEndpointBody[endpointName] = { endpoint: obj.endpoint.replace('?', loanNumber) };
        if (endpointName === 'property') {
          finalEndpointBody[endpointName].endpoint = finalEndpointBody[endpointName].endpoint + propCode;
        }
        if (obj.reqBody && obj.reqBody.type === 'get') {
          finalEndpointBody[endpointName] = addToObj(finalEndpointBody[endpointName], obj.reqBody.body);
        }
      }
      return finalEndpointBody;
    },
    [loanNumber, propCode],
  );

  const getGetReq = useCallback(async () => {
    let copyObj = {};
    const promises = [];
    for (const endpoint in endpointsAccessed) {
      promises.push(getGetEndpointReqBody(endpoint));
    }
    await Promise.all(promises).then((endpointsGetReqs) => {
      copyObj = addToObj(copyObj, endpointsGetReqs);
    });
    return copyObj;
  }, [getGetEndpointReqBody]);

  const getVariableUpdateReq = useCallback(
    async (varInfo: varInfo, uniqueStr: string = '') => {
      let name = varInfo.setTo ? varInfo.setTo : varInfo.name;
      if (!isStrEmptyOrNull(uniqueStr)) name = uniqueStr + name;
      if (varInfo.actions.includes('post') && isChanged(name)) {
        let variableUpdateData = {};
        const val = getValForVarType(getNestedVal(loanDefaultInfo, name), varInfo.type);
        const reqname = varInfo.reqVarName ? varInfo.reqVarName : varInfo.name;
        variableUpdateData[reqname] = val;
        return variableUpdateData;
      }
      return null;
    },
    [isChanged, loanDefaultInfo],
  );

  const getParamsToAdd = useCallback(
    (varInfo: varInfo, uniqueStr: string = '') => {
      let nameOfVal = varInfo.setTo ? varInfo.setTo : varInfo.name;
      if (!isStrEmptyOrNull(uniqueStr)) nameOfVal = uniqueStr + nameOfVal;
      const paramsToAdd = varInfo.actions.includes('url')
        ? getValForVarType(getNestedVal(loanDefaultInfo, nameOfVal), varInfo.type)
        : '';
      let returnObj = {};
      returnObj[nameOfVal] = paramsToAdd;
      return returnObj;
    },
    [loanDefaultInfo],
  );

  const getUpdateEndpointReqData = useCallback((endpointName: string, variablesUpdateReqs) => {
    const obj = endpointsAccessed[endpointName];
    let endpointUpdateDataObj = addToObj(null, variablesUpdateReqs);
    if (endpointUpdateDataObj !== null) {
      if (obj.reqBody && obj.reqBody.type === 'post') {
        endpointUpdateDataObj = addToObj(endpointUpdateDataObj, obj.reqBody.body);
      }
    }
    return endpointUpdateDataObj;
  }, []);

  const getEndpointURLString = useCallback(
    (obj, paramsToAdd) => {
      let endpointStr = obj.endpoint.replace('?', loanNumber);
      for (const param in paramsToAdd) {
        if (!isStrEmptyOrNull(paramsToAdd[param])) {
          endpointStr += paramsToAdd[param];
        }
      }
      return endpointStr;
    },
    [loanNumber],
  );

  const getEndpointUpdateReq = useCallback(
    async (endpointName: string, uniqueStr: string = '') => {
      const obj = endpointsAccessed[endpointName];
      let finalEndpointUpdateReq = null;
      let paramsToAdd = {};
      const promises = [];
      obj.variables.forEach((varInfo) => {
        promises.push(getVariableUpdateReq(varInfo, uniqueStr));
        paramsToAdd = addToObj(paramsToAdd, [getParamsToAdd(varInfo, uniqueStr)]);
      });
      await Promise.all(promises).then((variablesUpdateReqs) => {
        finalEndpointUpdateReq = {};
        const endpointUpdateDataObj = getUpdateEndpointReqData(endpointName, variablesUpdateReqs);
        if (endpointUpdateDataObj !== null) {
          let endpointStr = getEndpointURLString(obj, paramsToAdd);

          finalEndpointUpdateReq[isStrEmptyOrNull(uniqueStr) ? endpointName : uniqueStr] = {
            endpoint: endpointStr,
            data: endpointUpdateDataObj,
          };
        }
      });
      return finalEndpointUpdateReq;
    },
    [loanNumber, getVariableUpdateReq, getUpdateEndpointReqData, getParamsToAdd, getEndpointURLString],
  );

  const isMultiUseEndpoint = useCallback((endpoint: string) => {
    if (!endpointsAccessed.hasOwnProperty(endpoint)) return false;
    if (!endpointsAccessed[endpoint].hasOwnProperty('endpointType')) return false;
    if (endpointsAccessed[endpoint].endpointType !== 'multi') return false;
    return true;
  }, []);

  const getUpdateReq = useCallback(async () => {
    try {
      let finalUpdateReq = null;
      const promises = [];
      for (const endpoint in endpointsAccessed) {
        if (isMultiUseEndpoint(endpoint) && loanDefaultInfo.hasOwnProperty(endpoint)) {
          for (const key in loanDefaultInfo[endpoint]) {
            let uniqueStr = endpoint + '.' + key + '.';
            promises.push(getEndpointUpdateReq(endpoint, uniqueStr));
          }
        } else if (
          !isMultiUseEndpoint(endpoint) &&
          (endpointsAccessed[endpoint].reqtype === 'post' || endpointsAccessed[endpoint].reqtype === 'all')
        ) {
          promises.push(getEndpointUpdateReq(endpoint));
        }
      }
      await Promise.all(promises).then((endpointsUpdateReqs) => {
        finalUpdateReq = addToObj(finalUpdateReq, endpointsUpdateReqs);
      });
      return finalUpdateReq;
    } catch (error) {
      console.log('Error getting update request in getUpdateReq of useLoanDefaultCodesInfo: ', error);
      return null;
    }
  }, [getEndpointUpdateReq, isMultiUseEndpoint, loanDefaultInfo]);

  // errors and statuses
  const [errorLoading, setErrorLoading] = useState('');
  const [errorSaving, setErrorSaving] = useState('');
  const [errorEndpoint, setErrorEndpoint] = useState('');
  const [loading, setLoading] = useState(false);

  const { get } = useAbortController();

  const handleGetRes = useCallback(
    async (respData: EndpointRespData, obj: EndpointDataAccessed) => {
      const varsToUpdate = obj.variables;
      varsToUpdate.forEach((varInfo) => {
        const name = varInfo.setTo ? varInfo.setTo : varInfo.name;
        try {
          //var currVal = getNestedVal(loanDefaultInfo, name);
          const val =
            respData.data && !varInfo.resetToEmtVal ? getValueFromEndpoint(respData.data, varInfo) : varInfo.emtVal; //currVal !== null ? currVal : varInfo.emtVal;
          handleChange('save', name, val);
        } catch (error) {
          console.log('Error loading ' + name + ' from endpoint ' + obj.endpoint + ': ' + error);
        }
      });
    },
    [handleChange],
  );

  const setErrorStates = useCallback((endpointWOError: boolean, type: string, errorSet: Set<string>) => {
    const errorStr = getErrorStr(errorSet);
    if (errorStr !== '') {
      if (!endpointWOError && type === 'get') {
        setErrorLoading(errorStr);
      }
      if (type === 'update') {
        setErrorSaving(errorStr);
      }
      if (endpointWOError && type === 'get') {
        setErrorEndpoint(errorStr);
      }
    }
  }, []);

  const clearErrorStates = useCallback(() => {
    setErrorLoading('');
    setErrorSaving('');
    setErrorEndpoint('');
  }, []);

  const handleResErrorsIfAny = useCallback(
    (res, type) => {
      let endpointWOError,
        overallError = false;
      const errorSet = new Set<string>();
      const reserr = getErrorFromEndpoint(res);
      if (!isStrEmptyOrNull(reserr)) {
        overallError = true;
        errorSet.add(reserr);
      }

      for (const endpoint in res) {
        const err = getErrorFromEndpoint(res, endpoint);
        if (isStrEmptyOrNull(err) && isEndpointLoanSpecific(endpoint)) {
          endpointWOError = true;
        }
        if (!isStrEmptyOrNull(err)) {
          errorSet.add(err);
        }
      }
      setErrorStates(endpointWOError && !overallError, type, errorSet);
    },
    [setErrorStates],
  );

  const handleUpdateRes = useCallback(
    (obj: EndpointDataAccessed) => {
      obj.variables.forEach((varInfo) => {
        if (varInfo.resetToEmtVal) {
          const name = varInfo.setTo ? varInfo.setTo : varInfo.name;
          const val = varInfo.emtVal;
          handleChange('save', name, val);
        }
      });
    },
    [handleChange],
  );

  const handleResValues = useCallback(
    async (res, type) => {
      const promises = [];
      for (const endpoint in res) {
        if (endpointsAccessed.hasOwnProperty(endpoint)) {
          promises.push(
            type === 'get'
              ? handleGetRes(res[endpoint], endpointsAccessed[endpoint])
              : handleUpdateRes(endpointsAccessed[endpoint]),
          );
        }
      }
      Promise.all(promises);
    },
    [handleGetRes, handleUpdateRes],
  );

  const loanDefaultInfoAPI = useCallback(
    async (type: string, body: object) => {
      if (!token) return;
      let request = { url: 'batch', data: body, method: 'post' };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const config = get();
      await axiosCall(request, config, headers)
        .then((res) => {
          if (!isObjEmptyOrNull(res)) {
            const promises = [];
            promises.push(handleResErrorsIfAny(res, type), handleResValues(res, type));
            Promise.all(promises);
          }
        })
        .catch((e) => {
          console.log('Error using loanDefaultInfoAPI in the useLoanDefaultCodesInfo hook: ', e);
          handleChange('clear');
          type === 'get' ? setErrorLoading(e) : setErrorSaving(e);
        });
    },
    [get, handleChange, handleResValues, handleResErrorsIfAny, token],
  );

  const getLDCInfo = useCallback(async () => {
    clearErrorStates();
    if (!isStrEmptyOrNull(loanNumber)) {
      setLoading(true);
      const getReq = await getGetReq();
      loanDefaultInfoAPI('get', getReq).finally(() => {
        setLoading(false);
      });
    }
  }, [loanDefaultInfoAPI, loanNumber, getGetReq, clearErrorStates]);

  const resetMultiUseEndpoints = useCallback(() => {
    handleChange('save', 'DDSDate', {});
    handleChange('save', 'investorDDSDate', {});
  }, [handleChange]);

  const updateLDCInfo = useCallback(async () => {
    clearErrorStates();
    if (!isStrEmptyOrNull(loanNumber)) {
      setLoading(true);
      const updatereq = await getUpdateReq();
      const getreq = await getGetReq();
      if (!isObjEmptyOrNull(updatereq)) {
        loanDefaultInfoAPI('update', updatereq).finally(() => {
          loanDefaultInfoAPI('get', getreq).finally(() => {
            resetMultiUseEndpoints();
            setLoading(false);
          });
        });
      } else {
        setLoading(false);
      }
    }
  }, [loanDefaultInfoAPI, loanNumber, getUpdateReq, getGetReq, clearErrorStates, resetMultiUseEndpoints]);

  useEffect(() => {
    const fetchData = async () => {
      await getLDCInfo();
    };
    fetchData().catch(console.error);
  }, [getLDCInfo]);

  var apis = {
    getLDCInfo,
    updateLDCInfo,
  };

  var statuses = {
    errorLoading,
    errorSaving,
    errorEndpoint,
    loading,
    hasChanges,
  };

  return { loanDefaultInfo, apis, statuses, handleChange };
};

export default useLoanDefaultCodesInfo;
